// Pure transforms for the Missed Stops Re-Attempt (Scenario 10) createNextmvRun.
// A stop coming out of locationRoutes carries the PREVIOUS stocking date; the
// reattempt folds it into the NEW dropOffDate. Per the backend contract:
//   - id: rewrite the date suffix to the new dropOffDate
//   - expectedDropoffDate: the ORIGINAL (missed) date
//   - startTimeWindow/endTimeWindow: on the NEW date (same time-of-day)
//   - lat/long: from location master data (coordsByLocationId), since
//     locationRoutes/LocationRoute carries no coordinates.

// Replace the leading "YYYY-MM-DD" of a "YYYY-MM-DD HH:MM[:SS]" datetime with
// `newDate`, preserving the time-of-day exactly (no timezone conversion —
// keeps the reference frame the field already uses). Returns '' when the input
// has no parseable date-bearing time, so callers treat it as "no window".
export function rebaseDateTimeToDate(dateTimeStr, newDate) {
  if (typeof dateTimeStr !== 'string' || !newDate) return '';
  const match = dateTimeStr
    .trim()
    .match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2})(:\d{2})?$/);
  if (!match) return '';
  const seconds = match[2] || ':00';
  return `${newDate} ${match[1]}${seconds}`;
}

function coordsAreValid(coords) {
  return coords && Number.isFinite(coords.lat) && Number.isFinite(coords.long);
}

// Build the missedStops payload from the auto-assign queue. Every entry that
// can't be fully resolved (no record / no coords / no window) is reported in
// `skipped` rather than silently dropped, so the caller can block and name it.
export function buildMissedStopReattemptInputs({
  queueEntries,
  missedStopsByRowKey,
  coordsByLocationId,
  dropOffDate,
}) {
  const missedStops = [];
  const skipped = [];
  const byRowKey = missedStopsByRowKey || {};
  const byLocationId = coordsByLocationId || {};

  (queueEntries || []).forEach((entry) => {
    const locationName = entry.locationName || entry.stopId || entry.rowKey;
    const skip = (reason) =>
      skipped.push({
        rowKey: entry.rowKey,
        stopId: entry.stopId,
        locationName,
        reason,
      });

    const stop = byRowKey[entry.rowKey];
    if (!stop) {
      skip('no matching missed-stop record');
      return;
    }
    const coords = byLocationId[entry.stopId];
    if (!coordsAreValid(coords)) {
      skip('missing coordinates');
      return;
    }
    const startTimeWindow = rebaseDateTimeToDate(
      stop.nextmvDeliveryWindowStart || stop.calculatedStartTime,
      dropOffDate,
    );
    const endTimeWindow = rebaseDateTimeToDate(
      stop.nextmvDeliveryWindowEnd || stop.calculatedEndTime,
      dropOffDate,
    );
    if (!startTimeWindow || !endTimeWindow) {
      skip('no resolvable delivery window');
      return;
    }

    missedStops.push({
      id: `${stop.id}#${stop.stockingIndex}-${dropOffDate}`,
      lat: coords.lat,
      long: coords.long,
      startTimeWindow,
      endTimeWindow,
      expectedDropoffDate: stop.stockingDate,
    });
  });

  return { missedStops, skipped };
}
